import { supabase } from '@/core/database/supabase';

export const workOrderEditService = {
  async updateWorkOrder(id: string, values: any) {
    // 1. Get current data for before_data
    const { data: beforeData } = await supabase
      .from('work_orders')
      .select('*')
      .eq('id', id)
      .single();

    // 2. Perform update
    const { collaborator_ids, ...updateValues } = values;

    const { data, error } = await supabase
      .from('work_orders')
      .update(updateValues)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // 3. Sync Collaborators
    if (collaborator_ids !== undefined) {
      // 3.1. Delete old members
      await supabase
        .from('work_order_members')
        .delete()
        .eq('work_order_id', id);

      // 3.2. Insert new members
      if (collaborator_ids && collaborator_ids.length > 0) {
        const memberPayloads = collaborator_ids.map((uid: string) => ({
          work_order_id: id,
          user_id: uid,
          role_code: 'member',
          company_id: data.company_id,
          project_id: data.project_id
        }));

        const { error: memberError } = await supabase
          .from('work_order_members')
          .insert(memberPayloads);

        if (memberError) {
          console.error('[workOrderEditService] Failed to sync collaborators:', memberError);
        }
      }
    }

    // 3. Record Audit Log
    try {
      const { auditService } = await import('@/services/audit.service').then(m => ({ auditService: m.default }));
      await auditService.log({
        project_id: data.project_id,
        company_id: data.company_id,
        action_code: 'update',
        module_code: 'work_orders',
        entity_type: 'work_orders',
        entity_id: id,
        summary: `Cập nhật lệnh thi công: ${data.work_order_code}`,
        before_data: beforeData as any,
        after_data: data as any
      });
    } catch (logErr) {
      console.error('[workOrderEditService] Failed to record audit log:', logErr);
    }

    return data;
  },

  async getReferenceData(companyId: string) {
    const [
      projectsResult,
      usersResult,
      teamsResult,
      categoriesResult,
      zonesResult,
      phasesResult,
    ] = await Promise.all([
      supabase
        .from('projects')
        .select('id, code, name')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name', { ascending: true }),
      supabase
        .from('user_profiles')
        .select('id, full_name, email')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('full_name', { ascending: true }),
      supabase
        .from('teams')
        .select('id, project_id, code, name')
        .eq('company_id', companyId)
        .order('name', { ascending: true }),
      supabase
        .from('categories')
        .select('id, code, name, parent_id, level, path')
        .or(companyId ? `company_id.eq.${companyId},company_id.is.null` : 'company_id.is.null')
        .eq('is_active', true)
        .order('display_order', { ascending: true }),
      supabase
        .from('project_zones')
        .select('id, project_id, zone_code, zone_name')
        .eq('company_id', companyId)
        .order('zone_name', { ascending: true }),
      supabase
        .from('project_phases')
        .select('id, project_id, code, name')
        .eq('company_id', companyId)
        .order('display_order', { ascending: true }),
    ]);

    const error = [
      projectsResult.error,
      usersResult.error,
      teamsResult.error,
      categoriesResult.error,
      zonesResult.error,
      phasesResult.error,
    ].find(Boolean);
    if (error) throw error;

    return {
      projects: (projectsResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
      })),
      users: (usersResult.data || []).map((item) => ({
        id: item.id,
        label: item.full_name || item.email || item.id,
      })),
      teams: (teamsResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
      categories: (categoriesResult.data || []).map((item: any) => ({
        id: item.id,
        label: item.name,
        code: item.code,
        parent_id: item.parent_id,
        level: item.level,
        path: item.path,
      })),
      zones: (zonesResult.data || []).map((item) => ({
        id: item.id,
        label: [item.zone_code, item.zone_name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
      phases: (phasesResult.data || []).map((item) => ({
        id: item.id,
        label: [item.code, item.name].filter(Boolean).join(' - '),
        project_id: item.project_id,
      })),
    };
  }
};
